export interface AnalyticsRow {
  event_id: string;
  sport_key: string;
  sport_title: string;
  region: string | null;
  bookmaker_key: string;
  bookmaker_title: string;
  market_key: 'h2h' | 'spreads' | 'totals' | string;
  outcome_name: string | null;
  outcome_point: number | null;
  market_last_update: string; // ISO timestamptz
  event_commence_time: string; // ISO timestamptz
  home_team: string | null;
  away_team: string | null;
  price_decimal: number | null;
  implied_prob_raw: number | null;
  market_overround: number | null;
  market_vig_pct: number | null;
  implied_prob_equal: number | null;
  fair_odds_equal: number | null;
  implied_prob_weighted: number | null;
  fair_odds_weighted: number | null;
  pinnacle_prob_devig: number | null;
  pinnacle_last_update: string | null;
  expected_value: number | null;
  expected_value_pct: number | null;
}

export interface FetchBetsParams {
  sportKey?: string;
  sportTitles?: string[];
  marketKey?: string;
  bookmakerKey?: string;
  bookmakerTitles?: string[];
  eventName?: string; // matches home/away
  homeTeam?: string; // filters home_team ilike
  awayTeam?: string; // filters away_team ilike
  evFilter?: 'all' | 'positive' | 'negative';
  isLive?: boolean; // computed from commence time
  region?: string;
  fromTime?: string; // ISO
  toTime?: string; // ISO
  limit?: number;
  offset?: number;
  sort?:
    | 'commence_time_asc'
    | 'commence_time_desc'
    | 'updated_desc'
    | 'updated_asc'
    | 'odds_asc'
    | 'odds_desc'
    | 'vig_asc'
    | 'vig_desc'
    | 'fair_odds_asc'
    | 'fair_odds_desc'
    | 'implied_prob_asc'
    | 'implied_prob_desc'
    | 'true_win_prob_asc'
    | 'true_win_prob_desc'
    | 'expected_value_asc'
    | 'expected_value_desc';
}

export const marketKeyToLabel = (marketKey: string): string => {
  if (marketKey === 'h2h') return 'Moneyline';
  if (marketKey === 'spreads') return 'Spread';
  if (marketKey === 'totals') return 'Total';
  return marketKey;
};

// The Bet interface from BetCard.tsx. Duplicated here to avoid circular deps.
export interface BetUIModel {
  id: string;
  sport: string;
  event: string;
  commenceTime: string;
  marketLastUpdate?: string;
  region?: string | null;
  bookmaker: string;
  market: string;
  outcome: string;
  odds: number;
  fairOdds: number | null;
  impliedProbability: number | null;
  trueWinProbability: number | null;
  vig: number | null;
  expectedValue: number | null;
  isLive: boolean;
}

export const buildSelectionId = (row: AnalyticsRow): string => {
  const parts = [
    row.event_id,
    row.sport_key,
    row.region ?? 'na',
    row.bookmaker_key,
    row.market_key,
    row.outcome_name ?? 'na',
    row.outcome_point === null || row.outcome_point === undefined ? 'na' : String(row.outcome_point)
  ];
  return parts.join('|');
};

export const mapAnalyticsRowToBet = (row: AnalyticsRow): BetUIModel => {
  const marketLabel = marketKeyToLabel(row.market_key);
  const isSpread = row.market_key === 'spreads';
  const isTotals = row.market_key === 'totals';
  const isH2H = row.market_key === 'h2h';

  const formattedOutcomePoint = (() => {
    const point = row.outcome_point;
    if (point === null || point === undefined) return '';

    // For Totals markets: no leading '+', round to 1 decimal place
    if (isTotals) {
      const roundedToOneDecimal = Math.round(point * 10) / 10;
      return `${roundedToOneDecimal.toFixed(1)}`;
    }

    // For Spread markets: keep '+' for positive, round to 1 decimal place
    if (isSpread) {
      const roundedToOneDecimal = Math.round(point * 10) / 10;
      const valueString = roundedToOneDecimal.toFixed(1);
      return point > 0 ? `+${valueString}` : `${valueString}`;
    }

    // Default formatting (e.g., Spreads): keep '+' for positive and show up to 2 decimals,
    // trimming trailing .00
    const valueString = point.toFixed(2).replace(/\.00$/, '');
    return point > 0 ? `+${valueString}` : `${valueString}`;
  })();

  const outcome = isH2H
    ? (row.outcome_name ?? '')
    : isSpread
      ? `${row.outcome_name ?? ''} ${formattedOutcomePoint}`.trim()
      : isTotals
        ? `${row.outcome_name ?? ''} ${formattedOutcomePoint}`.trim()
        : (row.outcome_name ?? '');

  const eventTitle = `${row.home_team ?? 'Home'} vs ${row.away_team ?? 'Away'}`;

  return {
    id: buildSelectionId(row),
    sport: row.sport_title || row.sport_key,
    event: eventTitle,
    commenceTime: row.event_commence_time,
    marketLastUpdate: row.market_last_update,
    region: row.region ?? null,
    bookmaker: row.bookmaker_title,
    market: marketLabel,
    outcome,
    odds: row.price_decimal ?? 0,
    fairOdds: row.fair_odds_weighted ?? row.fair_odds_equal ?? null,
    impliedProbability: row.implied_prob_weighted ?? row.implied_prob_equal ?? row.implied_prob_raw ?? null,
    trueWinProbability: row.pinnacle_prob_devig ?? null,
    vig: row.market_vig_pct ?? null,
    expectedValue: row.expected_value ?? null,
    isLive: new Date(row.event_commence_time).getTime() <= Date.now(),
  };
};


